import { applyPrintTokens, sheetSizeMm } from './printLayout';
import { downloadBlob, safePrintFileName } from './printDownload';

const MM_TO_PT = 72 / 25.4;

function concatBytes(parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  parts.forEach((p) => {
    out.set(p, off);
    off += p.length;
  });
  return out;
}

function jpegToPdf(pages, pageWpt, pageHpt) {
  const encoder = new TextEncoder();
  const chunks = [];
  const offsets = [];
  let pos = 0;
  const push = (bytes) => {
    chunks.push(bytes);
    pos += bytes.length;
  };
  const pushStr = (s) => push(encoder.encode(s));
  const beginObj = (num) => {
    offsets[num] = pos;
  };

  pushStr('%PDF-1.4\n');
  beginObj(1);
  pushStr('1 0 obj<< /Type /Catalog /Pages 2 0 R >>endobj\n');
  const kids = pages.map((_, i) => `${3 + i * 3} 0 R`).join(' ');
  beginObj(2);
  pushStr(`2 0 obj<< /Type /Pages /Count ${pages.length} /Kids [${kids}] >>endobj\n`);

  pages.forEach((page, i) => {
    const pageNo = 3 + i * 3;
    const imgNo = pageNo + 1;
    const contentNo = pageNo + 2;
    const content = `q ${pageWpt.toFixed(2)} 0 0 ${pageHpt.toFixed(2)} 0 0 cm /Im${i} Do Q\n`;
    beginObj(pageNo);
    pushStr(
      `${pageNo} 0 obj<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWpt.toFixed(
        2
      )} ${pageHpt.toFixed(2)}] /Resources << /XObject << /Im${i} ${imgNo} 0 R >> >> /Contents ${contentNo} 0 R >>endobj\n`
    );
    beginObj(imgNo);
    pushStr(
      `${imgNo} 0 obj<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.jpeg.length} >>stream\n`
    );
    push(page.jpeg);
    pushStr('\nendstream\nendobj\n');
    beginObj(contentNo);
    pushStr(
      `${contentNo} 0 obj<< /Length ${content.length} >>stream\n${content}endstream\nendobj\n`
    );
  });

  const xrefAt = pos;
  const size = offsets.length;
  const lines = ['xref', `0 ${size}`, '0000000000 65535 f '];
  for (let n = 1; n < size; n += 1) {
    lines.push(`${String(offsets[n]).padStart(10, '0')} 00000 n `);
  }
  pushStr(`${lines.join('\n')}\ntrailer<< /Size ${size} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`);
  return concatBytes(chunks);
}

function dataUrlToBytes(dataUrl) {
  const b64 = String(dataUrl).split(',')[1] || '';
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function fitText(ctx, text, maxW) {
  let s = String(text ?? '');
  if (!s) return '';
  if (ctx.measureText(s).width <= maxW) return s;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > maxW) s = s.slice(0, -1);
  return `${s}…`;
}

function bandText(cell, ctx) {
  if (!cell) return '';
  return applyPrintTokens(cell.text, ctx);
}

/**
 * 미리보기와 같은 표 레이아웃을 캔버스→JPEG→PDF 로 내려받는다.
 * (한글 폰트 임베드 없이 OS 글꼴로 그려 파일 다운로드가 되게 한다)
 */
export function downloadPrintPdf(model, layout, ctx = {}, fileName = '목록출력') {
  const columns = model?.columns || [];
  if (!columns.length) return { ok: false, reason: '출력할 항목을 선택하세요.' };

  const sheet = sheetSizeMm(layout);
  const margin = layout?.marginMm || { top: 15, right: 15, bottom: 15, left: 15 };
  const pageWpt = sheet.w * MM_TO_PT;
  const pageHpt = sheet.h * MM_TO_PT;
  const scale = 2;
  const pxPerMm = (96 / 25.4) * scale;
  const canvasW = Math.round(sheet.w * pxPerMm);
  const canvasH = Math.round(sheet.h * pxPerMm);
  const ml = Number(margin.left) || 15;
  const mr = Number(margin.right) || 15;
  const mt = Number(margin.top) || 15;
  const mb = Number(margin.bottom) || 15;
  const contentWmm = Math.max(40, sheet.w - ml - mr);
  const showNo = layout?.showRowNumber !== false;
  const widths = layout?.columnWidthsMm || {};
  const rowNoMm = Number(layout?.rowNumberWidthMm) || 12;
  const font = layout?.fontFamily || 'Malgun Gothic';
  const fontSizePt = Number(layout?.fontSizePt) || 9;
  const rows = model?.rows || [];
  const mergeMap = model?.mergeMap || null;
  const title = layout?.title || model?.gridLabel || '목록';

  const fields = showNo
    ? [{ field: '__rowNo', header: 'No.', align: 'center', mm: rowNoMm }, ...columns]
    : columns.slice();
  const named = showNo ? columns : fields;
  let used = showNo ? rowNoMm : 0;
  named.forEach((c) => {
    const w = Number(widths[c.field]);
    used += Number.isFinite(w) && w > 0 ? w : 0;
  });
  const leftover = Math.max(8, contentWmm - used);
  const unset = named.filter((c) => !(Number(widths[c.field]) > 0));
  const colMm = fields.map((c) => {
    if (c.field === '__rowNo') return rowNoMm;
    const w = Number(widths[c.field]);
    if (Number.isFinite(w) && w > 0) return w;
    return leftover / Math.max(1, unset.length);
  });

  const printCtxBase = {
    today: ctx.today,
    screenName: ctx.screenName || title,
    tenantName: ctx.tenantName || '',
    tenantCode: ctx.tenantCode || '',
    userName: ctx.userName || '',
  };

  const rowHmm = Math.max(6, fontSizePt * 0.45);
  const titleHmm = fontSizePt * 0.7 + 4;
  const bandHmm = 8;

  const makeCanvas = () => {
    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const c2d = canvas.getContext('2d');
    c2d.fillStyle = '#fff';
    c2d.fillRect(0, 0, canvasW, canvasH);
    c2d.strokeStyle = '#222';
    c2d.fillStyle = '#111';
    return { canvas, c2d };
  };

  const mmx = (mm) => (ml + mm) * pxPerMm;
  const mmy = (mm) => (mt + mm) * pxPerMm;

  const pages = [];
  let rowIdx = 0;
  let pageNo = 0;
  const estPages = Math.max(1, Math.ceil(rows.length / 20));

  while (pageNo === 0 || rowIdx < rows.length) {
    pageNo += 1;
    const printCtx = { ...printCtxBase, page: pageNo, pageCount: Math.max(estPages, pageNo) };
    const { canvas, c2d } = makeCanvas();
    let yMm = 0;

    const drawBand = (band) => {
      const w = contentWmm / 3;
      ['left', 'center', 'right'].forEach((pos, i) => {
        const cell = band?.[pos];
        const text = bandText(cell, printCtx);
        if (!text) return;
        const size = Number(cell?.fontSizePt) || fontSizePt;
        c2d.font = `${size}pt "${font}", "Malgun Gothic", sans-serif`;
        c2d.textAlign = cell?.align || pos;
        const x =
          cell?.align === 'right' || pos === 'right'
            ? mmx((i + 1) * w) - 2
            : cell?.align === 'center' || pos === 'center'
              ? mmx(i * w + w / 2)
              : mmx(i * w) + 2;
        c2d.textBaseline = 'top';
        c2d.fillText(fitText(c2d, text, w * pxPerMm - 4), x, mmy(yMm));
      });
      yMm += bandHmm;
    };

    drawBand(layout?.header);
    c2d.font = `bold ${fontSizePt + 5}pt "${font}", "Malgun Gothic", sans-serif`;
    c2d.textAlign = 'center';
    c2d.textBaseline = 'top';
    c2d.fillText(fitText(c2d, title, contentWmm * pxPerMm), mmx(contentWmm / 2), mmy(yMm));
    yMm += titleHmm;

    const fontPx = `${fontSizePt}pt "${font}", "Malgun Gothic", sans-serif`;
    const headerFont = `bold ${fontSizePt}pt "${font}", "Malgun Gothic", sans-serif`;

    const drawRow = (cells, header) => {
      let xMm = 0;
      c2d.font = header ? headerFont : fontPx;
      cells.forEach((cell, i) => {
        const w = colMm[i];
        const x = mmx(xMm);
        const y = mmy(yMm);
        const pw = w * pxPerMm;
        const ph = rowHmm * pxPerMm;
        if (header) {
          c2d.fillStyle = '#eee';
          c2d.fillRect(x, y, pw, ph);
          c2d.fillStyle = '#111';
        }
        c2d.strokeRect(x, y, pw, ph);
        const align = cell.align || (header ? 'center' : 'left');
        c2d.textAlign = align;
        c2d.textBaseline = 'middle';
        const tx = align === 'right' ? x + pw - 4 : align === 'center' ? x + pw / 2 : x + 4;
        c2d.fillText(fitText(c2d, cell.text, pw - 8), tx, y + ph / 2);
        xMm += w;
      });
      yMm += rowHmm;
    };

    drawRow(
      fields.map((c, i) => ({
        text: c.field === '__rowNo' ? 'No.' : c.header || c.field,
        align: 'center',
        i,
      })),
      true
    );

    const bottomLimit = sheet.h - mt - mb - bandHmm - 2;
    if (!rows.length && pageNo === 1) {
      c2d.font = fontPx;
      c2d.textAlign = 'center';
      c2d.textBaseline = 'middle';
      const y = mmy(yMm);
      c2d.strokeRect(mmx(0), y, contentWmm * pxPerMm, rowHmm * pxPerMm);
      c2d.fillText('조회된 자료가 없습니다', mmx(contentWmm / 2), y + (rowHmm * pxPerMm) / 2);
      yMm += rowHmm;
    }
    while (rowIdx < rows.length && yMm + rowHmm <= bottomLimit) {
      const row = rows[rowIdx];
      const cells = [];
      fields.forEach((c) => {
        if (c.field === '__rowNo') {
          cells.push({ text: String(row.index + 1), align: 'center' });
          return;
        }
        const info = mergeMap?.[c.field]?.[row.index];
        if (info?.skip) {
          cells.push({ text: '', align: 'left' });
          return;
        }
        const align = c.align === 'right' || c.type === 'number' ? 'right' : c.align || 'left';
        cells.push({ text: row.cells[c.field] ?? '', align });
      });
      drawRow(cells, false);
      rowIdx += 1;
    }

    yMm = Math.max(yMm + 2, sheet.h - mt - mb - bandHmm);
    drawBand(layout?.footer);

    pages.push({
      width: canvas.width,
      height: canvas.height,
      jpeg: dataUrlToBytes(canvas.toDataURL('image/jpeg', 0.84)),
    });
    if (!rows.length) break;
    if (rowIdx >= rows.length) break;
    if (pageNo > 200) break;
  }

  const pdf = jpegToPdf(pages, pageWpt, pageHpt);
  downloadBlob(new Blob([pdf], { type: 'application/pdf' }), safePrintFileName(fileName, 'pdf'));
  return { ok: true };
}
